import { apiClient } from './client';

export interface TemplateAttribute {
  id: string;
  componentId: string;
  key: string;
  value: string | null;
  orderIndex: number;
}

export type TemplateCommentKind = 'note' | 'limitation';

export interface TemplateComponentComment {
  id: string;
  componentId: string;
  kind: TemplateCommentKind;
  body: string;
  orderIndex: number;
}

export interface TemplateComponent {
  id: string;
  sectionId: string;
  name: string;
  orderIndex: number;
  attributes: TemplateAttribute[];
  comments: TemplateComponentComment[];
}

export interface TemplateSection {
  id: string;
  templateId: string;
  name: string;
  orderIndex: number;
  isCustomized: boolean;
  /**
   * Per-section Standards of Practice body. NULL means "no SoP for this
   * section" — renders nothing under the section header. Edits snapshot
   * into report_sections.sop_body at generate time.
   */
  sopBody: string | null;
  components: TemplateComponent[];
}

export type TemplateSource = 'default' | 'custom' | 'duplicated' | 'spectora_import';

export interface Template {
  id: string;
  inspectorId: string;
  name: string;
  source: TemplateSource;
  isDefault: boolean;
  sections: TemplateSection[];
  introDismissed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateSummary {
  id: string;
  name: string;
  source: TemplateSource;
  isDefault: boolean;
  sectionCount: number;
  componentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface SpectoraImportResult {
  template: Template;
  counts: {
    sections: number;
    components: number;
    defects: number;
    /** Short label + value rows (`Material: Asphalt shingles`) that became attributes. */
    infoFacts: number;
    /** Info rows without a short label that became prose notes on the component. */
    infoNotes: number;
    /** `limit` rows that became limitation comments on the component. */
    limitations: number;
    rowsIgnored: number;
    deficienciesInserted: number;
    /**
     * Rows that hit the `(inspector_id, section, component, title)` unique
     * index and were skipped. Non-zero on re-imports of the same XLSX.
     */
    deficienciesSkipped: number;
  };
}

interface ApiEnvelope<T> { success: boolean; data: T; error?: string }

async function unwrap<T>(p: Promise<ApiEnvelope<T>>): Promise<T> {
  const res = await p;
  if (!res.success) throw new Error(res.error ?? 'Request failed');
  return res.data;
}

export const templatesApi = {
  getMine: () => unwrap(apiClient.get<ApiEnvelope<Template>>('/templates/mine')),

  list: () => unwrap(apiClient.get<ApiEnvelope<TemplateSummary[]>>('/templates')),

  getById: (id: string) =>
    unwrap(apiClient.get<ApiEnvelope<Template>>(`/templates/${id}`)),

  create: (name: string) =>
    unwrap(apiClient.post<ApiEnvelope<Template>>('/templates', { name })),

  duplicate: (id: string, name?: string) =>
    unwrap(apiClient.post<ApiEnvelope<Template>>(`/templates/${id}/duplicate`, { name })),

  setDefault: (id: string) =>
    unwrap(apiClient.post<ApiEnvelope<Template>>(`/templates/${id}/default`)),

  remove: (id: string) =>
    unwrap(apiClient.delete<ApiEnvelope<undefined>>(`/templates/${id}`)),

  importSpectora: (file: File) =>
    unwrap(
      apiClient.uploadFile<ApiEnvelope<SpectoraImportResult>>(
        '/templates/import/spectora',
        file,
      ),
    ),

  rename: (id: string, name: string) =>
    unwrap(apiClient.patch<ApiEnvelope<Template>>(`/templates/${id}`, { name })),

  restoreDefaults: (id: string) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>(
        `/templates/${id}/restore-defaults`,
        undefined,
        { headers: { 'X-Confirm-Destructive': 'restore-all' } },
      ),
    ),

  dismissIntro: () =>
    unwrap(apiClient.post<ApiEnvelope<Template>>('/templates/intro/dismiss')),

  createSection: (templateId: string, name: string) =>
    unwrap(apiClient.post<ApiEnvelope<Template>>(`/templates/${templateId}/sections`, { name })),

  // ── sections ────────────────────────────────────────
  updateSection: (
    id: string,
    patch: { name?: string; order_index?: number },
  ) => unwrap(apiClient.patch<ApiEnvelope<Template>>(`/template-sections/${id}`, patch)),

  deleteSection: (id: string) =>
    unwrap(apiClient.delete<ApiEnvelope<Template>>(`/template-sections/${id}`)),

  reorderSections: (templateId: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>('/template-sections/reorder', {
        template_id: templateId,
        ids,
      }),
    ),

  restoreSection: (id: string) =>
    unwrap(apiClient.post<ApiEnvelope<Template>>(`/template-sections/${id}/restore-default`)),

  // Null clears the per-section SoP (renders nothing under the section
  // header in the report). To get the seed default back, either call
  // `restoreSopDefault` (SoP only) or use `restoreSection` (resets the
  // whole section).
  updateSopBody: (id: string, body: string | null) =>
    unwrap(apiClient.patch<ApiEnvelope<Template>>(`/template-sections/${id}`, { sop_body: body })),

  // Rewrites just the section's `sop_body` from the matching Flowspect
  // seed (direct or alias). Returns 400 if no seed exists for the
  // section name — the editor button is hidden in that case.
  restoreSopDefault: (id: string) =>
    unwrap(apiClient.post<ApiEnvelope<Template>>(`/template-sections/${id}/sop/restore-default`)),

  createComponent: (sectionId: string, name: string) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>(`/template-sections/${sectionId}/components`, { name }),
    ),

  // ── components ──────────────────────────────────────
  updateComponent: (id: string, patch: { name?: string; order_index?: number }) =>
    unwrap(apiClient.patch<ApiEnvelope<Template>>(`/template-components/${id}`, patch)),

  deleteComponent: (id: string) =>
    unwrap(apiClient.delete<ApiEnvelope<Template>>(`/template-components/${id}`)),

  reorderComponents: (sectionId: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>('/template-components/reorder', {
        section_id: sectionId,
        ids,
      }),
    ),

  createAttribute: (
    componentId: string,
    input: { key: string; value?: string | null },
  ) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>(
        `/template-components/${componentId}/attributes`,
        input,
      ),
    ),

  // ── attributes ──────────────────────────────────────
  updateAttribute: (
    id: string,
    patch: { key?: string; value?: string | null; order_index?: number },
  ) => unwrap(apiClient.patch<ApiEnvelope<Template>>(`/template-attributes/${id}`, patch)),

  deleteAttribute: (id: string) =>
    unwrap(apiClient.delete<ApiEnvelope<Template>>(`/template-attributes/${id}`)),

  reorderAttributes: (componentId: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>('/template-attributes/reorder', {
        component_id: componentId,
        ids,
      }),
    ),

  // ── comments (notes + limitations) ─────────────────────
  createComment: (
    componentId: string,
    input: { kind: TemplateCommentKind; body: string; order_index?: number },
  ) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>(
        `/template-components/${componentId}/comments`,
        input,
      ),
    ),

  updateComment: (
    id: string,
    patch: { kind?: TemplateCommentKind; body?: string; order_index?: number },
  ) => unwrap(apiClient.patch<ApiEnvelope<Template>>(`/template-comments/${id}`, patch)),

  deleteComment: (id: string) =>
    unwrap(apiClient.delete<ApiEnvelope<Template>>(`/template-comments/${id}`)),

  reorderComments: (componentId: string, ids: string[]) =>
    unwrap(
      apiClient.post<ApiEnvelope<Template>>('/template-comments/reorder', {
        component_id: componentId,
        ids,
      }),
    ),
};

export type { ApiEnvelope };
